import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';



const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const { login, isAuthenticated, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Get the location state from redirect
  const from = location.state?.from?.pathname || '/dashboard';


  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard', { replace: false });
    }
  }, [isAuthenticated, loading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic validation
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }

    setLoadingSubmit(true);

    try {
      await login({
        username: email,
        password: password
      });
      // Redirect to the page the user was trying to access
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.detail || 'Failed to login. Please check your credentials.');
    } finally {
      setLoadingSubmit(false);
    }
  };



  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardHeader className="space-y-1 pb-4">
        <h2 className="text-xl font-semibold text-white text-center">Sign In</h2>
        <p className="text-sm text-gray-400 text-center">
          Enter your credentials to access the dashboard
        </p>
      </CardHeader>
      <CardContent>

        {error && (
          <div className="mb-4 rounded border border-red-500 bg-red-100 px-4 py-3 text-red-800 flex items-center" role="alert">
            <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12A9 9 0 1 1 3 12a9 9 0 0 1 18 0Z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-white">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-white">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
              required
            />
          </div>
          <Button
            type="submit"
            className="w-full bg-red-600 hover:bg-red-700 text-white"
          >
            {loadingSubmit ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                loading...
              </>
            ) : <>Sign In</>}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default Login;